import { ApiResponse as APIResponse } from '../../shared/types';
import {
  UserOnboardingProgress,
  UserOnboardingStatus,
  UserOnboardingStepId,
} from '../types';
import { supabase } from './supabase';

export const FIRST_TIME_ONBOARDING_FLOW_KEY = 'restaurant_first_time_setup';

export const PRIMARY_FIRST_TIME_ONBOARDING_STEP_IDS: UserOnboardingStepId[] = [
  'outlets',
  'departments',
  'menu',
  'sections_tables',
  'section_printers',
  'takeaway_printers',
  'first_order',
];

export const SECONDARY_FIRST_TIME_ONBOARDING_STEP_IDS: UserOnboardingStepId[] =
  ['users', 'security'];

const sanitizeProgress = (
  value: Record<string, any> | null,
): UserOnboardingProgress | null => {
  if (!value) {
    return null;
  }

  return {
    completed_at: value.completed_at || null,
    completed_steps: Array.isArray(value.completed_steps)
      ? (value.completed_steps as UserOnboardingStepId[])
      : [],
    current_step: (value.current_step as UserOnboardingStepId | null) || null,
    flow_key: value.flow_key || FIRST_TIME_ONBOARDING_FLOW_KEY,
    last_seen_at: value.last_seen_at || new Date().toISOString(),
    started_at: value.started_at || new Date().toISOString(),
    status: (value.status as UserOnboardingStatus) || 'in_progress',
  };
};

const getAuthenticatedUserId = async (): Promise<string> => {
  const {
    data: { user },
    error,
  } = await supabase.auth.getUser();

  if (error || !user) {
    throw new Error('You must be authenticated to continue onboarding.');
  }

  return user.id;
};

const getNextPendingPrimaryStep = (
  completedSteps: UserOnboardingStepId[],
): UserOnboardingStepId | null =>
  PRIMARY_FIRST_TIME_ONBOARDING_STEP_IDS.find(
    (stepId) => !completedSteps.includes(stepId),
  ) || null;

const getDerivedStatus = (
  completedSteps: UserOnboardingStepId[],
): UserOnboardingStatus =>
  PRIMARY_FIRST_TIME_ONBOARDING_STEP_IDS.every((stepId) =>
    completedSteps.includes(stepId),
  )
    ? 'completed'
    : 'in_progress';

export const ensureFirstTimeOnboardingProgress = async (
  currentStep?: UserOnboardingStepId | null,
): Promise<APIResponse<UserOnboardingProgress>> => {
  try {
    const userId = await getAuthenticatedUserId();

    const { data: existingRecord, error: existingError } = await supabase
      .from('user_onboarding_progress')
      .select('*')
      .eq('user_id', userId)
      .eq('flow_key', FIRST_TIME_ONBOARDING_FLOW_KEY)
      .maybeSingle();

    if (existingError) {
      return { success: false, error: existingError.message, data: undefined };
    }

    if (existingRecord) {
      const nextProgress = sanitizeProgress(existingRecord);

      if (!nextProgress) {
        return {
          success: false,
          error: 'Unable to prepare onboarding progress.',
          data: undefined,
        };
      }

      const nextCurrentStep =
        nextProgress.status === 'completed'
          ? nextProgress.current_step
          : currentStep ||
            nextProgress.current_step ||
            getNextPendingPrimaryStep(nextProgress.completed_steps);

      const { data: updatedRecord, error: updateError } = await supabase
        .from('user_onboarding_progress')
        .update({
          current_step: nextCurrentStep,
          last_seen_at: new Date().toISOString(),
        })
        .eq('user_id', userId)
        .eq('flow_key', FIRST_TIME_ONBOARDING_FLOW_KEY)
        .select('*')
        .single();

      if (updateError) {
        return { success: false, error: updateError.message, data: undefined };
      }

      return {
        success: true,
        data: sanitizeProgress(updatedRecord) || undefined,
      };
    }

    const { data, error } = await supabase
      .from('user_onboarding_progress')
      .insert({
        current_step: currentStep || PRIMARY_FIRST_TIME_ONBOARDING_STEP_IDS[0],
        flow_key: FIRST_TIME_ONBOARDING_FLOW_KEY,
        status: 'in_progress',
        user_id: userId,
      })
      .select('*')
      .single();

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    return { success: true, data: sanitizeProgress(data) || undefined };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to initialize onboarding progress',
      data: undefined,
    };
  }
};

export const saveFirstTimeOnboardingProgress = async (
  progress: UserOnboardingProgress,
): Promise<APIResponse<UserOnboardingProgress>> => {
  try {
    const userId = await getAuthenticatedUserId();

    const { data, error } = await supabase
      .from('user_onboarding_progress')
      .update({
        completed_at: progress.completed_at,
        completed_steps: progress.completed_steps,
        current_step: progress.current_step,
        last_seen_at: new Date().toISOString(),
        status: progress.status,
      })
      .eq('user_id', userId)
      .eq('flow_key', progress.flow_key)
      .select('*')
      .single();

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    return { success: true, data: sanitizeProgress(data) || undefined };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to save onboarding progress',
      data: undefined,
    };
  }
};

export const updateFirstTimeOnboardingStep = async (
  progress: UserOnboardingProgress,
  stepId: UserOnboardingStepId,
  completed: boolean,
): Promise<APIResponse<UserOnboardingProgress>> => {
  const completedSteps = completed
    ? Array.from(new Set([...progress.completed_steps, stepId]))
    : progress.completed_steps.filter(
        (existingStepId) => existingStepId !== stepId,
      );

  const nextStatus = getDerivedStatus(completedSteps);
  const nextProgress: UserOnboardingProgress = {
    ...progress,
    completed_at: nextStatus === 'completed' ? new Date().toISOString() : null,
    completed_steps: completedSteps,
    current_step:
      nextStatus === 'completed'
        ? null
        : getNextPendingPrimaryStep(completedSteps),
    last_seen_at: new Date().toISOString(),
    status: nextStatus,
  };

  return saveFirstTimeOnboardingProgress(nextProgress);
};

export const setFirstTimeOnboardingCurrentStep = async (
  progress: UserOnboardingProgress,
  stepId: UserOnboardingStepId | null,
): Promise<APIResponse<UserOnboardingProgress>> =>
  saveFirstTimeOnboardingProgress({
    ...progress,
    current_step: stepId,
    last_seen_at: new Date().toISOString(),
  });

export const completeFirstTimeOnboarding = async (
  progress: UserOnboardingProgress,
): Promise<APIResponse<UserOnboardingProgress>> =>
  saveFirstTimeOnboardingProgress({
    ...progress,
    completed_at: new Date().toISOString(),
    current_step: null,
    last_seen_at: new Date().toISOString(),
    status: 'completed',
  });
